"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, DollarSign, Percent, Award, CircleCheck as CheckCircle2, ArrowUpRight, ShieldCheck, Search } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const metrics = [{
  id: 1,
  value: "+420%",
  label: "Organic Traffic Growth",
  subtext: "Organic traffic growth achieved over the 8-month engagement via targeted keyword strategy and technical SEO.",
  icon: TrendingUp,
  badge: "Search Traffic"
}, {
  id: 2,
  value: "+280%",
  label: "Organic Revenue Increase",
  subtext: "Organic revenue increase driven by improved on-page optimization, search intent alignment, and conversion focus.",
  icon: DollarSign,
  badge: "Commercial Impact"
}, {
  id: 3,
  value: "+65%",
  label: "Conversion Rate Improvement",
  subtext: "Conversion rate improvement achieved through strategic CTA refinement and checkout process simplification.",
  icon: Percent,
  badge: "Intent Matching"
}, {
  id: 4,
  value: "Top 5",
  label: "Rankings for 25+ Target Keywords",
  subtext: "Top 5 rankings secured for 25+ high-intent target keywords from structured content gap and keyword mapping work.",
  icon: Award,
  badge: "Keyword Visibility"
}];
const highlights = ["Sustainable non-brand traffic expansion across high-value product categories", "Tailored search strategies built on clean data and honest technical audits", "Transparent reporting with measurable commercial outcomes, not vanity metrics"];
export default function Results() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="verifiedresults" data-nav="light" className="bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary border border-primary/20">
              <ShieldCheck className="w-3.5 h-3.5 mr-1" />
              Verified Performance
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground">
            Tangible Search Outcomes, <br className="hidden sm:inline" />
            <span className="text-primary">Measured in Revenue & Visibility</span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            I deliver sustainable, measurable SEO consulting for ambitious UK and international brands. 
            Here is a snapshot of verified client outcomes achieved through direct, hands-on optimization.
          </motion.p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} variants={cardVariants} data-index={i}>
                <Card className="h-full border border-border/80 hover:border-primary/40 bg-card transition-[border-color,box-shadow] duration-300 shadow-sm hover:shadow-md flex flex-col justify-between">
                  <CardContent className="p-6 sm:p-7 flex flex-col h-full justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-6">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <Badge variant="outline" className="text-xs font-medium bg-background text-muted-foreground border-border">
                          {item.badge}
                        </Badge>
                      </div>

                      <div className="space-y-2">
                        <div className="text-3.5xl sm:text-4xl font-extrabold font-serif tracking-tight text-primary">
                          {item.value}
                        </div>
                        <h3 className="text-base sm:text-lg font-semibold font-default text-foreground leading-snug">
                          {item.label}
                        </h3>
                      </div>
                    </div>

                    <p className="mt-4 pt-4 border-t border-border/60 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {item.subtext}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mt-12 lg:mt-16 rounded-2xl bg-secondary/30 border border-border p-8 sm:p-10 lg:p-12 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
                <Search className="w-4 h-4" />
                <span>Verified Business Impact</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
                Compounded commercial revenue driven by organic search.
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Through precise technical restructuring and intent-driven content mapping, the retailer unlocked sustainable search visibility that continuously feeds bottom-line ecommerce revenue.
              </p>
              
              <ul className="space-y-2.5 pt-2">
                {highlights.map((highlight, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2.5 text-sm sm:text-base text-foreground font-medium">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>{highlight}</span>
                  </li>)}
              </ul>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-4 justify-center lg:items-end">
              <Button asChild size="lg" className="w-full sm:w-auto lg:w-full font-medium shadow-sm">
                <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                  <span>Explore Verified Case Studies</span>
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto lg:w-full font-medium">
                <Link to="/ContactPage" className="inline-flex items-center justify-center">
                  Book a Free SEO Consultation
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}